import React, { Component } from 'react';
import PropTypes from 'prop-types';
import KeyHandler, { KEYUP } from 'react-key-handler';
import { findIndex, first, last, delay, random, trim, trimEnd, trimStart, find, reduce } from 'lodash';
import {
  FormControl, FormGroup, HelpBlock,
  Tooltip, OverlayTrigger, Button,
} from 'react-bootstrap';
import Fa from 'react-fontawesome';
import { ThreeDots } from 'svg-loaders-react';
import questionTypes from './question-types';
import styles from '../sidebox/styles.css';
import fieldStyles from './styles.css';

const LIMIT = 12;

class FeedbackField extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      options: null,
      error: null,
      hoverOption: null,
      value: '',
      showInstruction: false,
    };
    this.selectOption = this.selectOption.bind(this);
    this.handleNavigateUp = this.handleNavigateUp.bind(this);
    this.handleNavigateDown = this.handleNavigateDown.bind(this);
    this.handleEnterKey = this.handleEnterKey.bind(this);
    this.handleTextChange = this.handleTextChange.bind(this);
    this.handleTextFieldKeyUp = this.handleTextFieldKeyUp.bind(this);
    this.handleDropboxSelect = this.handleDropboxSelect.bind(this);
  }

  componentWillMount() {
    const typingDelay = new Promise((resolve) => {
      delay(() => {
        resolve();
      }, this.props.feedback.type === questionTypes.QUESTION_TYPE_STATIC ? 10 : random(1, 1) * 1000); // 1 or 2 seconds
    });
    if (this.props.feedback.type === questionTypes.QUESTION_TYPE_PICKLIST) {
      this.setState({ loading: true, error: null });
      const promises = [this.props.getOptions(this.props.feedback), typingDelay];
      Promise.all(promises)
        .then(
          ([options]) => {
            const firstOption = options.length <= LIMIT ? first(options) : null;
            this.setState({
              options,
              hoverOption: firstOption,
              loading: false,
              errors: null,
            },
              () => {
                this.props.onOptionLoaded(options);
                this.focusTextField();
              });
          },
          ([error]) => {
            this.setState({ options: [], loading: false, error });
          },
        );
    } else {
      typingDelay.then(() => {
        this.setState({ options: [], value: '', loading: false, errors: null }, () => {
          if (this.props.feedback.type === questionTypes.QUESTION_TYPE_STATIC) {
            // to to next
            this.selectOption(null);
          }
          this.props.onOptionLoaded(null);
          this.focusTextField();
        });
      });
    }
  }

  focusTextField() {
    if (this.input) {
      this.input.focus();
    }
  }

  handleTextChange(e) {
    this.setState({ value: e.target.value });
  }

  handleTextFieldKeyUp(e) {
    const { feedback: { multipleLines } } = this.props;
    const newValue = trimStart(trimEnd(trim(e.target.value), '\n'), '\n');

    if ((e.keyCode === 13 && !multipleLines) || (e.keyCode === 13 && multipleLines && e.shiftKey)) {
      // TODO: reject last \n
      // TODO: defocus text field
      if (newValue && newValue.length > 0) {
        this.selectOption({ value: newValue });
      }
    } else {
      const prevValue = this.state.showInstruction;
      this.setState({ showInstruction: true }, () => {
        if (!prevValue || multipleLines) {
          this.props.onOptionLoaded(null); // for scrolling
        }
      });
    }
  }

  handleDropboxSelect(e) {
    const { options } = this.state;
    const { value } = e.target;
    if (value) {
      const selected = find(options, { value });
      if (selected) {
        this.setState({ hoverOption: selected }, () => {
          this.selectOption(selected);
        });
      }
    }
  }

  handleNavigateUp() {
    const { hoverOption, options } = this.state;
    let nextHoverOption = null;
    if (!hoverOption) {
      nextHoverOption = last(options);
    } else {
      const idx = findIndex(options, { value: hoverOption.value });
      const nextIdx = idx === 0 ? options.length - 1 : idx - 1;
      nextHoverOption = options[nextIdx];
    }
    this.setState({ hoverOption: nextHoverOption });

  }
  handleNavigateDown() {
    const { hoverOption, options } = this.state;
    let nextHoverOption = null;
    if (!hoverOption) {
      nextHoverOption = first(options);
    } else {
      const idx = findIndex(options, { value: hoverOption.value });
      const nextIdx = idx === options.length - 1 ? 0 : idx + 1;
      nextHoverOption = options[nextIdx];
    }
    this.setState({ hoverOption: nextHoverOption });
  }

  handleEnterKey() {
    if (this.props.feedback.type === questionTypes.QUESTION_TYPE_PICKLIST) {
      if (this.state.hoverOption) {
        this.props.selectOption(this.props.feedback, this.state.hoverOption);
      }
    } else if (this.props.feedback.type === questionTypes.QUESTION_TYPE_TEXT) {
      if (this.state.value && this.state.value.length) {
        this.props.selectOption(this.props.feedback, { value: this.state.value });
      }
    }
  }

  selectOption(option) {
    this.props.selectOption(this.props.feedback, option);
  }

  renderKeyHandlers() {
    const { disabled } = this.props;
    if (disabled) {
      return null;
    }

    const comps = [];

    if (this.props.feedback.type === questionTypes.QUESTION_TYPE_PICKLIST) {
      comps.push((
        <KeyHandler
          key="Enter"
          keyEventName={KEYUP}
          keyValue="Enter"
          onKeyHandle={this.handleEnterKey}
        />
      ));
      comps.push((
        <KeyHandler
          key="Up"
          keyEventName={KEYUP}
          keyValue="Up"
          onKeyHandle={this.handleNavigateUp}
        />
      ));
      comps.push((
        <KeyHandler
          key="ArrowUp"
          keyEventName={KEYUP}
          keyValue="ArrowUp"
          onKeyHandle={this.handleNavigateUp}
        />
      ));
      comps.push((
        <KeyHandler
          key="Down"
          keyEventName={KEYUP}
          keyValue="Down"
          onKeyHandle={this.handleNavigateDown}
        />
      ));
      comps.push((
        <KeyHandler
          key="ArrowDown"
          keyEventName={KEYUP}
          keyValue="ArrowDown"
          onKeyHandle={this.handleNavigateDown}
        />
      ));
    }
    return comps;
  }

  renderPickListDropdown() {
    const { hoverOption, options } = this.state;
    const { disabled, feedback: { optionText, response } } = this.props;

    if (disabled) {
      return (
        <div className={styles.fromMe}>
          <div className={styles.bubble}>
            {options.map((option) => {
              const active = option.value === response;
              if (!active) {
                return null;
              }
              return (
                <div key={option.value}>
                  {option.label}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.fromMe}>
        {this.renderKeyHandlers()}
        <div className={styles.bubble}>
          <FormGroup className={`animated fadeIn ${fieldStyles.selectField}`}>
            <FormControl
              componentClass="select"
              disabled={disabled}
              value={hoverOption ? hoverOption.value : null}
              onChange={this.handleDropboxSelect}
              inputRef={(input) => { this.input = input; }}
            >
              <option value="">{(optionText || 'Please select an option')}</option>
              {options.map(option => (<option className={fieldStyles.option} key={option.value} value={option.value}>{option.label}</option>))}
            </FormControl>
          </FormGroup>
        </div>
      </div>
    );
  }

  renderPickList() {
    const { feedback: { response }, disabled } = this.props;
    const { hoverOption, options } = this.state;
    const tooLong = options.length > LIMIT;
    if (tooLong) {
      return this.renderPickListDropdown();
    }
    return (
      <div className={styles.fromMe}>
        {this.renderKeyHandlers()}
        {options.map((option) => {
          const active = option.value === response;
          const over = !disabled && hoverOption && hoverOption.value === option.value;
          const hasDescription = option.description && option.description.length > 0;

          const tip = hasDescription ? (
            <Tooltip id={option.value}>
              <div>{option.description}</div>
            </Tooltip>
          ) : null;
          
          if (disabled) {
            if (!active) {
              return null;
            }
            
            return (
              <div key={option.value} className={styles.bubble}>
                {option.label}
              </div>
            );
          }
          return (
            <div
              key={option.value}
              role="checkbox"
              aria-checked={active}
              className={`${styles.bubble} ${(over ? styles.bubbleActive : '')}`}
              onMouseEnter={() => { this.setState({ hoverOption: option }); }}
              onMouseLeave={() => { this.setState({ hoverOption: null }); }}
              onClick={disabled ? () => {} : () => { this.selectOption(option); }}
              style={{ cursor: disabled ? 'default' : 'pointer' }}
            >
              <table
                className="table table-condensed animated fadeIn"
                style={{ margin: 0 }}
              >
                <tbody>
                  <tr>
                    <td width="30" style={{ border: 'none' }}>
                      {over && <Fa name="chevron-right" className="animated slideInLeft infinite" />}
                      {!over && <Fa name="square-o" />}
                    </td>
                    {hasDescription && <OverlayTrigger trigger={['hover', 'focus']} placement="left" overlay={tip}><td style={{ border: 'none' }}>{option.label}</td></OverlayTrigger>}
                    {!hasDescription && <td style={{ border: 'none' }}><span>{option.label}</span></td>}
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  }

  renderTextField() {
    const { feedback: { multipleLines, response }, disabled } = this.props;
    const { showInstruction } = this.state;
    const instructions = [];
    if (multipleLines) {
      instructions.push((
        <div key="first">
          <small><em>press <strong>Shift + Enter</strong> to continue</em></small>
        </div>
      ));
    } else {
      instructions.push((
        <div key="second">
          <small><em>press <strong>Enter</strong> to continue</em></small>
        </div>
      ));
    }
    
    let innerComp = null;

    if (!disabled) {
      innerComp = (
        <div className={styles.bubble}>
          <FormGroup className={`animated fadeIn ${fieldStyles.textField}`}>
            <FormControl
              componentClass={multipleLines ? 'textarea' : 'input'}
              placeholder="type something..."
              disabled={disabled}
              value={this.state.value}
              onChange={this.handleTextChange}
              onKeyUp={this.handleTextFieldKeyUp}
              inputRef={(input) => { this.input = input; }}
            />
            {showInstruction &&
              <div className="clearfix">
                <HelpBlock className="pull-left">{instructions}</HelpBlock>
                <div className="pull-right" style={{ paddingTop: 3 }}>
                  <Button onClick={this.handleEnterKey} disabled={(!this.state.value || this.state.value.length === 0)} bsSize="xs" bsStyle="default">Send</Button>
                </div>
              </div>
            }
          </FormGroup>
        </div>
      );
    } else if (multipleLines) {
      const lines = reduce((response || '').split('\n'), (items, text, id) => {
        if (trim(text).length > 0) {
          items.push({ id, text });
        }
        return items;
      }, []);
      innerComp = lines.map(line => (<div key={line.id} className={styles.bubble}>{line.text}</div>));
    } else {
      innerComp = (
        <div className={styles.bubble}>{response}</div>
      );
    }

    return (
      <div className={styles.fromMe}>
        {innerComp}
      </div>
    );
  }

  renderHTML(raw) {
    return <span dangerouslySetInnerHTML={{ __html: raw }} />;
  }

  renderLabel(text) {
    if (typeof text === 'string') {
      return this.renderHTML(text);
    }
    return text;
  }

  render() {
    const { loading, options } = this.state;
    if (loading) {
      return (
        <div className={styles.chats}>
          <div className={styles.fromThem}>
            <div className={`${styles.bubble} ${styles.typing} fadeIn`}>
              <ThreeDots className="animated fadeIn" fill="#ccc" width="60" />
            </div>
          </div>
        </div>
      );
    }
    const { feedback } = this.props;
    const { type, labels } = feedback;
    let comp = null;
    if (type === questionTypes.QUESTION_TYPE_PICKLIST) {
      comp = this.renderPickList();
    } else if (type === questionTypes.QUESTION_TYPE_TEXT) {
      comp = this.renderTextField();
    }
    return (
      <div className={styles.chats}>
        {labels !== undefined && labels !== null &&
          <div className={styles.fromThem}>
            {labels.map(label => ((<div className={styles.bubble} key={label.id}><div className="animated fadeIn">{this.renderLabel(label.text)}</div></div>)))}
            {type === questionTypes.QUESTION_TYPE_PICKLIST && options.length === 0 &&
              <div
                className={styles.bubble}
              >
                Hmmm... Looks like something went wrong. No options available. Please try again later.
              </div>
            }
          </div>
        }
        {comp}
      </div>
    );
  }
}

FeedbackField.propTypes = {
  feedback: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    })),
    paramName: PropTypes.string,
    type: PropTypes.oneOf([
      questionTypes.QUESTION_TYPE_PICKLIST,
      questionTypes.QUESTION_TYPE_TEXT,
      questionTypes.QUESTION_TYPE_STATIC,
    ]),
    response: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.bool,
    ]),
    optionText: PropTypes.string,
  }).isRequired,
  getOptions: PropTypes.func,
  selectOption: PropTypes.func,
  onOptionLoaded: PropTypes.func,
  disabled: PropTypes.bool,
};
FeedbackField.defaultProps = {
  response: null,
  getOptions: () => Promise.reject(new Error('need handler')),
  selectOption: () => {},
  onOptionLoaded: () => {},
  disabled: false,
};
FeedbackField.contextTypes = {
};
export default FeedbackField;
